import type { MenuItem } from 'primereact/menuitem';

export type ProductEdition = 'full' | 'boxed';

function readEdition(): ProductEdition {
  const raw = process.env.REACT_APP_PRODUCT_EDITION?.toLowerCase().trim();
  return raw === 'boxed' ? 'boxed' : 'full';
}

export const productEdition: ProductEdition = readEdition();

export const isBoxedEdition = productEdition === 'boxed';

/**
 * Фичи, отключаемые в коробочной редакции.
 *
 * Для коробки 2.0 список пуст: в неё входит весь функционал демо-стенда.
 * Пока список пуст, REACT_APP_PRODUCT_EDITION ничего не переключает —
 * сборки full и boxed дают идентичный UI.
 *
 * Чтобы исключить фичу из коробки, раскомментируйте (или добавьте по образцу)
 * по строке здесь и в маппингах ниже. Образец — реестр средних скоростей,
 * как он был исключён в коробке 1.0. Подробнее — boxedVersionInstruction.md.
 *
 * ВНИМАНИЕ по типизации: пока объект пуст, тип может быть только
 * Record<string, boolean> (`keyof typeof` схлопнулся бы в never). Такой тип
 * не ловит опечатки: `editionFeatures.someTypo` пройдёт компиляцию, вернёт
 * undefined и молча скроет функционал. Когда появятся реальные ключи —
 * верните строгую типизацию:
 *   export const editionFeatures = { ... } as const;
 *   export type ProductFeatureKey = keyof typeof editionFeatures;
 * и уберите `?? true` в isPageEnabledByProductEdition и
 * filterMenuItemsByProductEdition: со строгой типизацией индексирование
 * снова станет тотальным, и правая часть станет мёртвым кодом.
 */
export const editionFeatures: Record<string, boolean> = {
  // avgTransportSpeedRegistry: !isBoxedEdition
};

export type ProductFeatureKey = string;

/** Коды страниц из обертки isPageVisible — какие страницы не входят в коробочную версию */
const PAGE_CODE_TO_FEATURE: Partial<Record<string, ProductFeatureKey>> = {
  // код страницы из isPageVisible('...') в App.tsx → ключ фичи
  // avgTransportSpeeds: 'avgTransportSpeedRegistry'
};

export function isPageEnabledByProductEdition(pageCode: string): boolean {
  const key = PAGE_CODE_TO_FEATURE[pageCode];
  if (!key) return true;
  // `?? true` — временно, пока editionFeatures пуст (см. комментарий над ним)
  return editionFeatures[key] ?? true;
}

/** Пункты бокового меню: url пункта меню — ключ фичи */
const MENU_URL_TO_FEATURE: Partial<Record<string, ProductFeatureKey>> = {
  // url пункта меню из CustomMenuItem.tsx → тот же ключ фичи
  // '/avgTransportSpeed': 'avgTransportSpeedRegistry'
};

export function filterMenuItemsByProductEdition(items: MenuItem[]): MenuItem[] {
  return items
    .filter((item) => {
      const url = typeof item.url === 'string' ? item.url : undefined;
      if (!url) return true;
      const feature = MENU_URL_TO_FEATURE[url];
      if (!feature) return true;
      // `?? true` — временно, пока editionFeatures пуст (см. комментарий над ним)
      return editionFeatures[feature] ?? true;
    })
    .map((item) => ({
      ...item,
      items: item.items
        ? filterMenuItemsByProductEdition(item.items as MenuItem[])
        : undefined
    }));
}
